import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, FlaskConical, LogOut, Pill, UserRound, Users, Briefcase } from "lucide-react";

export default function DashboardLayout() {
    const { user, logout } = useAuth();
    const nav = useNavigate();

    const doLogout = () => {
        logout();
        nav("/login");
    };

    const isAdmin = user?.role === "admin";

    const navItems = isAdmin
        ? [
            { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
            { to: "/drugs", label: "Drug Intelligence", icon: FlaskConical, testid: "nav-drugs" },
            { to: "/users", label: "Salespeople", icon: Users, testid: "nav-users" },
        ]
        : [
            { to: "/my-queue", label: "My Queue", icon: Briefcase, testid: "nav-my-queue" },
            { to: "/drugs", label: "Drug Intelligence", icon: FlaskConical, testid: "nav-drugs" },
        ];

    return (
        <div className="min-h-screen bg-[#F8F7F4] rcn-grain flex">
            {/* Sidebar */}
            <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-[#E2E8F0] bg-white">
                <Link to={isAdmin ? "/dashboard" : "/my-queue"} className="flex items-center gap-3 px-6 h-16 border-b border-[#E2E8F0]">
                    <div className="w-9 h-9 rounded-lg bg-[#1A4331] flex items-center justify-center">
                        <Pill className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <p className="font-heading font-semibold text-[#1A4331] leading-tight">RCN Pharma Intel</p>
                        <p className="text-[10px] uppercase tracking-widest text-[#8A9A86]">Sales Intelligence</p>
                    </div>
                </Link>

                <nav className="flex-1 p-4 space-y-1">
                    {navItems.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            data-testid={item.testid}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                                    isActive
                                        ? "bg-[#1A4331] text-white"
                                        : "text-[#1E293B] hover:bg-[#F1F5F9]"
                                }`
                            }
                        >
                            <item.icon className="w-4 h-4" />
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="p-4 border-t border-[#E2E8F0]">
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-[#F8F7F4]">
                        <div className="w-9 h-9 rounded-full bg-[#1A4331] text-white flex items-center justify-center">
                            <UserRound className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[#1E293B] truncate" data-testid="sidebar-user-name">
                                {user?.full_name}
                            </p>
                            <p className="text-xs text-[#475569] capitalize">{user?.role}</p>
                        </div>
                    </div>
                    <Button
                        variant="ghost"
                        onClick={doLogout}
                        className="w-full justify-start gap-2 mt-2 text-[#475569] hover:text-[#991B1B]"
                        data-testid="sidebar-logout-btn"
                    >
                        <LogOut className="w-4 h-4" /> Sign out
                    </Button>
                </div>
            </aside>

            {/* Mobile topbar */}
            <div className="md:hidden fixed top-0 inset-x-0 bg-white border-b border-[#E2E8F0] z-20">
                <div className="flex items-center justify-between px-4 h-14">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-md bg-[#1A4331] flex items-center justify-center">
                            <Pill className="w-4 h-4 text-white" />
                        </div>
                        <p className="font-heading font-semibold text-[#1A4331]">RCN Pharma Intel</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={doLogout} data-testid="mobile-logout-btn">
                        <LogOut className="w-4 h-4" />
                    </Button>
                </div>
                <nav className="flex gap-1 px-2 pb-2">
                    {navItems.map((item) => (
                        <NavLink key={item.to} to={item.to} className={({ isActive }) =>
                            `flex-1 text-center py-2 text-xs rounded-md ${isActive ? "bg-[#1A4331] text-white" : "text-[#475569]"}`
                        } data-testid={`${item.testid}-mobile`}>
                            {item.label}
                        </NavLink>
                    ))}
                </nav>
            </div>

            <main className="flex-1 pt-24 md:pt-0 overflow-x-hidden">
                <Outlet />
            </main>
        </div>
    );
}
